import type { UseQueryResult } from '@tanstack/react-query';
import type { ConversationDetail, MessagingApartmentCard, PropertyResponse } from '@/shared/api';
import { PropertyContextCard } from './PropertyContextCard';
import { ReservationApartmentsCard } from './ReservationApartmentsCard';

const NOTE = 'border-hairline border-b px-4 py-3 text-[13px]';

/**
 * Apartment und Zugang einer Unterhaltung, oder die Apartments ihrer Reservierung.
 *
 * Die Kartenmenge hat Vorrang, sobald sie mehr als ein Apartment umfasst: eine teil-stornierte
 * Reservierung trägt beides — eine eindeutige Bindung auf das verbliebene Apartment und ein
 * storniertes Kind; die Einzelkarte verschluckte dessen Existenz. Fehler und Laden stehen vor der
 * Einzelkarte, sonst verschwände ein gescheiterter Abruf hinter einer Karte, die vollständig wirkt.
 */
export function ApartmentContext({
  thread,
  property,
  propertyFailed = false,
  onRetryProperty,
  apartments,
  canViewPii,
  demo,
}: {
  thread: ConversationDetail;
  property: PropertyResponse | null;
  /** Das Apartment konnte nicht geladen werden — der Abschnitt verschwindet dann nicht still. */
  propertyFailed?: boolean;
  onRetryProperty?: () => void;
  apartments: UseQueryResult<MessagingApartmentCard[]>;
  canViewPii: boolean;
  demo: boolean;
}) {
  const cards = apartments.data ?? [];
  if (cards.length > 1) {
    return (
      <ReservationApartmentsCard threadId={thread.id} apartments={cards} canViewPii={canViewPii} />
    );
  }
  if (thread.otaReservationId && apartments.isError) {
    return (
      <button
        type="button"
        onClick={() => void apartments.refetch()}
        className={`${NOTE} text-sealwax w-full text-left font-semibold`}
      >
        Apartments konnten nicht geladen werden – erneut versuchen
      </button>
    );
  }
  if (thread.otaReservationId && apartments.isLoading) {
    return <p className={`${NOTE} text-ink-faint`}>Apartments werden geladen …</p>;
  }
  if (property) {
    return (
      <PropertyContextCard
        property={property}
        threadId={thread.id}
        bookingId={thread.bookingId}
        demo={demo}
      />
    );
  }
  if (propertyFailed) {
    return (
      <button
        type="button"
        onClick={onRetryProperty}
        className={`${NOTE} text-sealwax min-h-11 w-full text-left font-semibold`}
      >
        Apartment und Zugang konnten nicht geladen werden – erneut versuchen
      </button>
    );
  }
  if (cards.length === 1) {
    return (
      <ReservationApartmentsCard threadId={thread.id} apartments={cards} canViewPii={canViewPii} />
    );
  }
  // Bewusst ohne Ursachenbehauptung: für einen ungescopten Admin gibt es keinen Zugriffsmangel.
  return thread.otaReservationId ? (
    <p className={`${NOTE} text-ink-soft`}>Keine Apartments zu dieser Reservierung.</p>
  ) : null;
}
